/**
 * FieldRenderer Component
 *
 * A unified component that routes to the correct renderer based on field type.
 * Supports display, edit, and inline modes with consistent styling.
 */

import type React from 'react';
import type { FieldDefinition, FieldMode, FieldRendererProps, FieldType } from '../../types/entityFramework';
import {
  AddressRenderer,
  BooleanRenderer,
  ComputedRenderer,
  DateRenderer,
  FileRenderer,
  JsonRenderer,
  LookupRenderer,
  NumberRenderer,
  SelectRenderer,
  SsnRenderer,
  TextRenderer,
} from './renderers';

// ============================================================================
// Types
// ============================================================================

export interface FieldRendererComponentProps extends FieldRendererProps {
  /** Use switch for boolean fields (default: true) */
  useSwitchForBoolean?: boolean;
  /** Upload handler for file fields */
  onUpload?: (file: File) => Promise<{ id: string; url: string; name: string }>;
  /** Accepted file types for file fields (e.g., "image/*,.pdf") */
  accept?: string;
  /** Maximum file size in bytes for file fields */
  maxSize?: number;
  /** Lookup options for lookup fields */
  lookupOptions?: Array<{ id: string; label: string; [key: string]: unknown }>;
  /** Async search handler for lookup fields */
  onLookupSearch?: (query: string) => Promise<Array<{ id: string; label: string }>>;
  /** Loading state for lookup fields */
  lookupLoading?: boolean;
  /** Show country in address fields */
  showCountry?: boolean;
}

// ============================================================================
// Field Type Mapping
// ============================================================================

type RendererType =
  | 'text'
  | 'number'
  | 'date'
  | 'boolean'
  | 'select'
  | 'file'
  | 'lookup'
  | 'address'
  | 'json'
  | 'computed'
  | 'ssn'
  | 'unsupported';

const getRendererType = (fieldType: FieldType): RendererType => {
  switch (fieldType) {
    // Text-based fields
    case 'TEXT':
    case 'TEXTAREA':
    case 'RICH_TEXT':
    case 'EMAIL':
    case 'URL':
    case 'PHONE':
      return 'text';

    // Number-based fields
    case 'NUMBER':
    case 'CURRENCY':
    case 'PERCENTAGE':
    case 'RATING':
      return 'number';

    // Date/time fields
    case 'DATE':
    case 'DATETIME':
    case 'TIME':
      return 'date';

    // Boolean fields
    case 'BOOLEAN':
      return 'boolean';

    // Select fields
    case 'SELECT':
    case 'MULTI_SELECT':
      return 'select';

    // File fields
    case 'FILE':
    case 'IMAGE':
    case 'SIGNATURE':
      return 'file';

    // Lookup fields
    case 'LOOKUP':
    case 'MULTI_LOOKUP':
      return 'lookup';

    // Address fields
    case 'ADDRESS':
      return 'address';

    // JSON fields
    case 'JSON':
      return 'json';

    // Computed fields (read-only)
    case 'CALCULATED':
    case 'ROLLUP':
    case 'FORMULA':
    case 'AUTO_NUMBER':
      return 'computed';

    // SSN/EIN masked fields (Issue #432)
    case 'SSN':
    case 'EIN':
      return 'ssn';

    default:
      return 'unsupported';
  }
};

// ============================================================================
// Unsupported Field Renderer
// ============================================================================

interface UnsupportedRendererProps {
  field: FieldDefinition;
  value: unknown;
  mode: FieldMode;
  className?: string;
}

const UnsupportedRenderer: React.FC<UnsupportedRendererProps> = ({ field, value, mode, className }) => {
  // In display mode, try to show the value as a string
  if (mode === 'display') {
    const displayValue = value != null ? String(value) : '-';
    return (
      <span className={className} style={{ color: 'rgba(128, 128, 128, 0.8)' }}>
        {displayValue}
      </span>
    );
  }

  // In edit/inline mode, show a placeholder
  return (
    <span className={className} style={{ color: 'rgba(128, 128, 128, 0.6)', fontStyle: 'italic' }}>
      [{field.fieldType} not supported]
    </span>
  );
};

// ============================================================================
// Component
// ============================================================================

export const FieldRenderer: React.FC<FieldRendererComponentProps> = ({
  field,
  value,
  mode,
  onChange,
  error,
  disabled,
  size = 'medium',
  neonAccent,
  className,
  useSwitchForBoolean = true,
  onUpload,
  accept,
  maxSize,
  lookupOptions,
  onLookupSearch,
  lookupLoading,
  showCountry,
}) => {
  const rendererType = getRendererType(field.fieldType);

  // Common props for all renderers
  const commonProps = {
    field,
    value,
    mode,
    onChange,
    error,
    disabled,
    size,
    neonAccent,
    className,
  };

  switch (rendererType) {
    case 'text':
      return <TextRenderer {...commonProps} />;

    case 'number':
      return <NumberRenderer {...commonProps} />;

    case 'date':
      return <DateRenderer {...commonProps} />;

    case 'boolean':
      return <BooleanRenderer {...commonProps} useSwitch={useSwitchForBoolean} />;

    case 'select':
      return <SelectRenderer {...commonProps} />;

    case 'file':
      return <FileRenderer {...commonProps} onUpload={onUpload} accept={accept} maxSize={maxSize} />;

    case 'lookup':
      return (
        <LookupRenderer {...commonProps} options={lookupOptions} onSearch={onLookupSearch} loading={lookupLoading} />
      );

    case 'address':
      return <AddressRenderer {...commonProps} showCountry={showCountry} />;

    case 'json':
      return <JsonRenderer {...commonProps} />;

    case 'computed':
      return <ComputedRenderer {...commonProps} />;

    case 'ssn':
      return <SsnRenderer {...commonProps} />;

    case 'unsupported':
    default:
      return <UnsupportedRenderer field={field} value={value} mode={mode} className={className} />;
  }
};

FieldRenderer.displayName = 'FieldRenderer';

export default FieldRenderer;
